import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { TypeOf, z } from "zod";
import { Profile } from "../../../../../@types/profile";
import DelendCentralizedCircularProgress from "../../../../../components/CircularProgress/DelendCentralizedCircularProgress";
import { useAppSelector } from "../../../../../hooks/hooks";
import AuthService from "../../../../../services/AuthService";
import ProfileService from "../../../../../services/ProfileService";
import ReturnButton from "../../ReturnButton";

const profileEmailInput = z.object({
  email: z
    .string()
    .nonempty({ message: "email é obrigatório" })
    .email({ message: "e-mail inválido" }),
});

type ProfileEmailInput = TypeOf<typeof profileEmailInput>;

const ProfileHeader = () => {
  return (
    <Box display={"flex"} flexDirection={"column"} gap={1}>
      <Typography variant="h2">E-mail de contato</Typography>

      <Typography>
        Este e-mail será utilizado para contato sobre informações importantes,
        para envio de códigos de segurança e recuperação de senha.
      </Typography>
    </Box>
  );
};

const ProfileEmailEditor: React.FC = () => {
  const navigate = useNavigate();

  const {
    register,
    clearErrors,
    formState: { errors, isSubmitting },
    handleSubmit,
  } = useForm<ProfileEmailInput>({
    resolver: zodResolver(profileEmailInput),
  });

  const profile = useAppSelector<Profile | undefined>(
    (state) => state.profileSlice.profile
  );

  const [countDown, setCountDownInSeconds] = useState(0);

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (countDown <= 0) {
      setCountDownInSeconds(0);
      return;
    }

    setTimeout(() => {
      setCountDownInSeconds((prev) => prev - 1);
    }, 1000);
  }, [countDown]);

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value;
    clearErrors("email");
    setHasChanges(newValue !== profile?.person.email);
  };

  if (!profile) return <DelendCentralizedCircularProgress />;

  const onSubmit: SubmitHandler<ProfileEmailInput> = async (
    profileEmailData
  ) => {
    try {
      setCountDownInSeconds(25);
      await ProfileService.updateProfile(profileEmailData, profile.person.uuid);
      await AuthService.refreshToken();
      toast.success(
        <>
          <h6>
            Enviamos um código de verificação para {profileEmailData.email}
          </h6>
          <span>
            Caso não encontre o e-mail, verifique suas pastas de spam ou de lixo
            eletrônico.
          </span>
        </>,
        {
          autoClose: 10000,
        }
      );
      navigate("/dashboard/profile");
    } catch (error: any) {
      toast.error(error.response.data.message);
      setCountDownInSeconds(0);
    }
  };

  return (
    <Box display={"flex"} gap={2}>
      <ReturnButton to="/dashboard/profile" />
      <Box
        display={"flex"}
        flexDirection={"column"}
        gap={2}
        component="form"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        width={"100%"}
      >
        <ProfileHeader />
        <TextField
          {...register("email")}
          error={!!errors.email}
          helperText={errors.email?.message}
          defaultValue={profile.person.email}
          onChange={handleTextChange}
          variant="standard"
          fullWidth
          label="e-mail"
          disabled={isSubmitting || countDown !== 0}
        />
        <Box display={"flex"} justifyContent={"end"}>
          <Button
            variant="outlined"
            size="large"
            disabled={isSubmitting || countDown !== 0 || !hasChanges}
            type="submit"
          >
            enviar e-mail de confirmação
            {countDown !== 0 && ` (${countDown}s)`}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfileEmailEditor;
